import { useRagnar } from '@mentimeter/ragnar-react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { FullSizeContainer } from '@mentimeter/visualizations-react/utils/full-size-container';
import {
  RatingGrid,
  type RatingGridData,
} from '@mentimeter/visualizations-react/visualizations/rating-grid';
import type { RatingGridChoice, RatingGridResult } from './types';

type BackgroundImage = {
  url: string;
} | null;

interface RatingGridVisualizationProps {
  shouldAnimate: boolean;
  results: RatingGridResult[];
  choices: RatingGridChoice[];
  range: { min: number; max: number };
  horizontalAxisLabel: string;
  verticalAxisLabel: string;
  backgroundImage: BackgroundImage;
}

export const RatingGridVisualization = ({
  shouldAnimate,
  results,
  choices,
  range,
  horizontalAxisLabel,
  verticalAxisLabel,
  backgroundImage,
}: RatingGridVisualizationProps) => {
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
      colors: { neutralWeak, bg },
      fonts: { body },
    },
  } = useRagnar();

  const colors = fillColors.length > 0 ? fillColors : [textColor];

  const data: RatingGridData[] = results.map((result) => ({
    id: result.id,
    score: {
      average: result.average ?? { x: 0, y: 0 },
      responses: result.responses ?? [],
    },
  }));

  const staticOptions = choices.map((choice, index) => ({
    id: choice.id,
    label: choice.label,
    dotNumber: (index + 1).toString(),
  }));

  const gridImageUrl = backgroundImage?.url ?? null;

  return (
    <Box width="100%" height="100%" px="4%">
      <FullSizeContainer>
        {(width, height) => (
          <RatingGrid
            width={width}
            height={height}
            colors={colors as [string, ...[string]]}
            textColor={textColor}
            backgroundColor={bg}
            image={
              gridImageUrl
                ? { url: gridImageUrl }
                : { placeholderImageColor: neutralWeak }
            }
            data={data}
            staticOptions={staticOptions}
            fontFamily={body}
            disableAnimations={!shouldAnimate}
            range={range}
            horizontalAxisLabel={horizontalAxisLabel}
            verticalAxisLabel={verticalAxisLabel}
          />
        )}
      </FullSizeContainer>
    </Box>
  );
};
